import * as Sentry from '@sentry/react';
import moment from 'moment';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { checkEmailAvailabilityUrl } from 'src/app/client/api';
import {
  acceptTos,
  accountCreateCard,
  accountDeleteAddress,
  accountDeleteCard,
  accountObtainMagicToken,
  accountSetUploadPhotoID,
  fetchAccount,
  fetchDeliveryAddress,
  fetchPatient,
  fetchPaymentCards,
  loadPHKDeliveryAddress,
  updateAccountLogin,
} from 'src/app/client/models/account';
import { createStripeToken } from 'src/app/client/stripe';
import { segmentTrack, sessionSetCurrentUser } from 'src/app/state/actions/index';
import { getCurrentAccountId, getCurrentUserId, getPrimaryPatientId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { AccountPatchRequestData, ContactInfo, PatientDependentDetails } from 'src/app/types';
import { convertToISODate, extractErrorCodes } from 'src/app/utils';

import {
  SET_ACCOUNT_ERROR,
  SET_ACCOUNT_IS_FETCHING,
  SET_ACCOUNT_SUCCESS,
  SET_DELIVERYADDRESS_ERROR,
  SET_DELIVERYADDRESS_IS_FETCHING,
  SET_DELIVERYADDRESS_SUCCESS,
  SET_FACILITY_ERROR,
  SET_FACILITY_IS_FETCHING,
  SET_FACILITY_SUCCESS,
  SET_PATIENT_ERROR,
  SET_PATIENT_IS_FETCHING,
  SET_PATIENT_SUCCESS,
  SET_PAYMENT_CARDS_DELETE_ERROR,
  SET_PAYMENT_CARDS_DELETE_SUCCESS,
  SET_PAYMENT_CARDS_ERROR,
  SET_PAYMENT_CARDS_IS_FETCHING,
  SET_PAYMENT_CARDS_SUCCESS,
  SET_PAYMENT_CARDS_UPDATE_ERROR,
  SET_PAYMENT_CARDS_UPDATE_SUCCESS,
  SET_PHARMAKON_ACCOUNT_UPDATED,
} from './types';

export function accountLoadEverything(withDeliveryAddress = true) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const state = getState();
      const { client } = state;

      const accountId = getCurrentAccountId(state);

      if (!accountId) {
        const error = new Error('Attempting to load account with no accountId present');
        throw error;
      }

      await Promise.all([
        withDeliveryAddress && loadPHKDeliveryAddress(client),
        dispatch(loadAccount()),
        dispatch(loadPatient()),
        dispatch(loadPaymentCards()),
        dispatch(loadFacility()),
      ]);

      dispatch({ type: SET_PHARMAKON_ACCOUNT_UPDATED });
    } catch (error) {
      Sentry.captureException(error);
    }
  };
}

// New Account Dispatches
export function fetchAccountPageData() {
  return (dispatch: Dispatch) => {
    dispatch(loadAccount());
    dispatch(loadPatient());
    dispatch(loadPaymentCards());
    dispatch(loadDeliveryAddress());
  };
}

export function fetchLoginData() {
  return async (dispatch: Dispatch) => {
    await dispatch(loadAccount());
    await dispatch(loadPatient());
  };
}

export function loadAccount(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: SET_ACCOUNT_IS_FETCHING,
    });
    try {
      const accountId = newAccountId ?? getCurrentAccountId(state);
      const accountResp = await fetchAccount(client, { accountId });
      dispatch({
        type: SET_ACCOUNT_SUCCESS,
        account: accountResp?.data,
      });
    } catch (err) {
      dispatch({
        type: SET_ACCOUNT_ERROR,
        error: err,
      });
    }
  };
}

export const patchAccount = (accountId: string, data: AccountPatchRequestData) => {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const { client } = getState();
    try {
      await client.patchAccount(accountId, data);
    } catch (err) {
      Sentry.captureException(err, {
        contexts: {
          request: {
            method: 'PATCH',
            accountId,
            data,
          },
        },
      });
      throw err;
    }
  };
};

export function loadPatient(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: SET_PATIENT_IS_FETCHING,
    });
    try {
      const accountId = newAccountId ?? getCurrentAccountId(state);

      if (accountId) {
        const patientsResp = await fetchPatient(client, { accountId });
        dispatch({
          type: SET_PATIENT_SUCCESS,
          patient: patientsResp?.data,
        });
      }
    } catch (err) {
      dispatch({
        type: SET_PATIENT_ERROR,
        error: err,
      });
    }
  };
}

function loadPaymentCards() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: SET_PAYMENT_CARDS_IS_FETCHING,
    });

    try {
      const accountId = getCurrentAccountId(state);
      const response = await fetchPaymentCards(client, { accountId });

      dispatch({
        type: SET_PAYMENT_CARDS_SUCCESS,
        paymentCards: response?.data,
      });
    } catch (err: unknown) {
      dispatch({
        type: SET_PAYMENT_CARDS_ERROR,
        error: err,
      });
    }
  };
}

export function postPaymentCard(stripe: any) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    const cardToken = await createStripeToken(stripe);

    try {
      const response = await accountCreateCard(client, { accountId, cardToken });

      dispatch({
        type: SET_PAYMENT_CARDS_UPDATE_SUCCESS,
        paymentCard: response?.data,
      });

      return response?.data?.id;
    } catch (err: unknown) {
      dispatch({
        type: SET_PAYMENT_CARDS_UPDATE_ERROR,
        error: err,
      });
    }
  };
}

export function deletePaymentCard(accountId: string, cardId: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const { client } = getState();

    try {
      await accountDeleteCard(client, { accountId, cardId });
      await dispatch(
        patchAccount(accountId, {
          stripeDefaultCardId: null,
        })
      );

      dispatch({
        type: SET_PAYMENT_CARDS_DELETE_SUCCESS,
        cardId,
      });
    } catch (err: unknown) {
      dispatch({
        type: SET_PAYMENT_CARDS_DELETE_ERROR,
        error: err,
      });
    }
  };
}

export function loadFacility(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({ type: SET_FACILITY_IS_FETCHING });
    try {
      const accountId = newAccountId ?? getCurrentAccountId(state);
      const facilityResp = await client.accountFacility(accountId);

      // TODO: Get coordinates some other way
      const eligibilityResp = await client.checkZipCodeEligibility(facilityResp?.data?.address.zipCode);
      const { latitude, longitude } = eligibilityResp?.data ?? {};

      dispatch({
        type: SET_FACILITY_SUCCESS,
        facility: {
          ...facilityResp?.data,
          address: {
            ...facilityResp?.data?.address,
            coordinates: {
              latitude,
              longitude,
            },
          },
        },
      });
    } catch (err) {
      dispatch({ type: SET_FACILITY_ERROR });
      Sentry.captureException(err);
    }
  };
}

export function loadDeliveryAddress() {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    dispatch({
      type: SET_DELIVERYADDRESS_IS_FETCHING,
    });
    try {
      const accountId = getCurrentAccountId(state);
      const deliveryAddressResp = await fetchDeliveryAddress(client, { accountId });
      dispatch({
        type: SET_DELIVERYADDRESS_SUCCESS,
        deliveryAddress: deliveryAddressResp?.data,
      });
    } catch (err) {
      dispatch({
        type: SET_DELIVERYADDRESS_ERROR,
        error: err,
      });
    }
  };
}

export function obtainMagicTokenAction() {
  return async (_: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const response = await accountObtainMagicToken(client);

    return response;
  };
}

export function deleteAccountAddress(addressId, accountId) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const response = await accountDeleteAddress(client, { addressId, accountId });

    await dispatch(loadDeliveryAddress());

    return response;
  };
}

export function setAccountImageID(file) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);

    const response = await accountSetUploadPhotoID(client, { accountId, file });

    return response;
  };
}

export function accountSetup(email: string, password: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const userId = getCurrentUserId(state);
    const accountId = getCurrentAccountId(state);

    await acceptTos(client, { accountId, userId });

    // TODO: Does this even need to exist? It supplies the data to `sessionSetCurrentUser`, but that happens in the `login` action too.
    const response = await updateAccountLogin(client, { userId, email, password });
    const user = response.data.data;
    await dispatch(sessionSetCurrentUser(user));
  };
}

export function currentlyLoggedInPatientHealthkitRecordsAvailable() {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const { client } = getState();

    const response = await client.patient.currentlyLoggedInPatientHealthkitRecordsAvailable();

    return response;
  };
}

export function createDependent(accountId: string, values: PatientDependentDetails) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const { client } = getState();

    const body = {
      firstName: values.firstName || null,
      lastName: values.lastName || null,
      sex: values.sex || null,
      birthdate: convertToISODate(values?.birthdate),
      isPrimary: false,
    };

    return await client.addPatientDependent(accountId, body);
  };
}

export function updatePatient(accountId, patientId, values) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const { client } = getState();
    const birthDateFormatted = moment(values?.birthdate, 'MM/DD/YYYY').format('YYYY-MM-DD');
    const attributes = {
      ...values,
      birthdate: birthDateFormatted,
    };
    const response = await client.patient.updatePatient(accountId, patientId, attributes);
    fetchPatient(client, { accountId });
    return response;
  };
}

export function updatePrimaryPatientInfo(
  payload: Partial<{
    allergies: string;
    medicalConditions: string;
    otherMedications: string;
    potentialTransfers: string;
  }>
) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    const accountId = getCurrentAccountId(state);
    const patientId = getPrimaryPatientId(state);

    return await client.patient.updatePatient(accountId, patientId, payload);
  };
}

export function emailCheck(email: string) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    const grecaptcha = window?.grecaptcha;

    const returnPromise = new Promise((res, rej) => {
      if (grecaptcha) {
        // fetch google captcha before running email check request
        // https://developers.google.com/recaptcha/docs/v3#programmatically_invoke_the_challenge
        grecaptcha.ready(() => {
          grecaptcha
            .execute('6LcyrVEfAAAAACITXKWQOR_9gP5otCO406KbZEvu', { action: 'email_check' })
            .then(async (captchaToken: string) => {
              res(await client.checkEmailAvailability({ email, captchaToken }));
            })
            .catch(error => rej(error));
        });
      } else {
        rej(new Error('Could not check email, failed to load google recaptcha'));
      }
    });

    return returnPromise;
  };
}

export function emailCheckV2Captcha(email: string, captchaToken: string) {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;

    try {
      const response = await client.checkEmailAvailability({ email, captchaToken, captchaVersion: 'v2' });
      return response;
    } catch (error) {
      const bffError = error?.error ?? '';
      const api = checkEmailAvailabilityUrl();
      const trackErrorPayload = {
        errorMessage: 'Check Email Availability Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(error),
        source: window.location.href,
      };

      segmentTrack(UNIFIED.error, trackErrorPayload);
    }
  };
}

export const updateContactInfo = ({ accountId, firstName, lastName, phone }: ContactInfo) => {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    try {
      await client.updateContactInfo(accountId, { firstName, lastName, phone });
    } catch (err) {
      Sentry.captureException(err);
    }
  };
};

export const patchDeliveryAddress = (deliveryAddressId, name) => {
  return async (_dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    try {
      await client.patchDeliveryAddress(deliveryAddressId, { name });
    } catch (err) {
      Sentry.captureException(err);
    }
  };
};
